import {create} from 'zustand';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

import {safeLocalStorage} from '../../../util/localStorage';
import {DEFAULT_DRAWER_WIDTH} from '../constants';

const wbAgentStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem: name => safeLocalStorage.getItem(name),
  setItem: (name, value) => {
    try {
      safeLocalStorage.setItem(name, value);
    } catch (e) {
      if (
        e instanceof DOMException &&
        (e.code === 22 || e.name === 'QuotaExceededError')
      ) {
        console.warn(`localStorage quota exceeded for "${name}"`, e);
        return;
      }
      console.warn(`Failed to persist "${name}" to localStorage`, e);
    }
  },
  removeItem: name => safeLocalStorage.removeItem(name),
};

interface WBAgentWindowState {
  // Agent chat open state
  open: boolean;
  // Root turn id to reopen on load, or null to stay closed.
  restoreThreadId: string | null;
  // Drawer width
  drawerWidth: number;
  historySidebarOpen: boolean;

  // Actions
  setOpen: (open: boolean) => void;
  setRestoreThreadId: (id: string | null) => void;
  setDrawerWidth: (width: number) => void;
  setHistorySidebarOpen: (open: boolean) => void;
}

interface PersistedWBAgentWindowPreferences {
  restoreThreadId: string | null;
  drawerWidth: number;
  historySidebarOpen: boolean;
}

interface LegacyPersistedWBAgentWindowPreferences extends Partial<PersistedWBAgentWindowPreferences> {
  open?: boolean;
  docked?: boolean;
  windowPosition?: {x: number; y: number} | null;
  windowWidth?: number;
  windowHeight?: number;
}

/**
 * useWBAgentWindowStore holds the WBAgent chat's window state, persisted to
 * localStorage.
 */
/* oxlint-disable wandb/zustand-enforce-slices-when-large-state -- flat window settings store where slicing adds no value */
export const useWBAgentWindowStore = create<WBAgentWindowState>()(
  devtools(
    persist<WBAgentWindowState, [], [], PersistedWBAgentWindowPreferences>(
      set => ({
        open: false,
        restoreThreadId: null,
        drawerWidth: DEFAULT_DRAWER_WIDTH,
        historySidebarOpen: false,

        setOpen: open => set({open}),
        setRestoreThreadId: id => set({restoreThreadId: id}),
        setDrawerWidth: width => set({drawerWidth: width}),
        setHistorySidebarOpen: historySidebarOpen => set({historySidebarOpen}),
      }),
      {
        name: 'wb-agent-window-preferences',
        storage: createJSONStorage(() => wbAgentStorage),
        version: 2,
        // v0 persisted `open`; v0 and v1 persisted floating-window settings.
        // Preserve only preferences supported by v2.
        migrate: persistedState => {
          const state =
            persistedState as LegacyPersistedWBAgentWindowPreferences | null;

          return {
            restoreThreadId: state?.restoreThreadId ?? null,
            drawerWidth: state?.drawerWidth ?? DEFAULT_DRAWER_WIDTH,
            historySidebarOpen: state?.historySidebarOpen ?? false,
          };
        },
        partialize: state => ({
          restoreThreadId: state.restoreThreadId,
          drawerWidth: state.drawerWidth,
          historySidebarOpen: state.historySidebarOpen,
        }),
      }
    ),
    {name: 'WBAgentWindowStore'}
  )
);
